import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FiSave, FiX } from 'react-icons/fi';
import { homeworkAPI } from '../../../services/api';
import StudentGroupPicker from '../../../components/StudentGroupPicker';
import './CreateHomeworkPage.css';

const CreateHomeworkPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const [description, setDescription] = useState('');
    const [students, setStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadStudents();
        if (isEditMode) {
            loadHomework();
        }
    }, [id]);

    const loadStudents = async () => {
        try {
            const response = await homeworkAPI.getStudentsList();
            setStudents(response.data.students || []);
        } catch (error) {
            console.error('Ошибка загрузки студентов:', error);
        }
    };

    const loadHomework = async () => {
        try {
            setLoading(true);
            const response = await homeworkAPI.getHomework(id);
            const hw = response.data.homework;
            const hwStudents = response.data.students || [];
            
            setDescription(hw.description);
            setSelectedStudents(hwStudents.map(s => s.id));
        } catch (error) {
            console.error('Ошибка загрузки домашнего задания:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!description.trim() || description === '<p><br></p>') {
            alert('Введите описание домашнего задания');
            return;
        }

        if (selectedStudents.length === 0 && selectedGroups.length === 0) {
            alert('Выберите хотя бы одного студента или группу');
            return;
        }

        try {
            setSaving(true);
            
            const data = {
                description,
                studentIds: selectedStudents,
                groupIds: selectedGroups
            };

            if (isEditMode) {
                await homeworkAPI.updateHomework(id, data);
            } else {
                await homeworkAPI.createHomework(data);
            }

            navigate('/admin/homework');
        } catch (error) {
            console.error('Ошибка сохранения домашнего задания:', error);
            alert('Ошибка при сохранении домашнего задания');
        } finally {
            setSaving(false);
        }
    };

    const toggleStudent = (studentId) => {
        setSelectedStudents(prev => 
            prev.includes(studentId)
                ? prev.filter(id => id !== studentId)
                : [...prev, studentId]
        );
    };

    const selectAllStudents = () => {
        setSelectedStudents(students.map(s => s.id));
    };

    const deselectAllStudents = () => {
        setSelectedStudents([]);
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],
            ['link'],
            ['clean']
        ]
    };

    if (loading) {
        return <div className="create-homework-loading">Загрузка...</div>;
    }

    return (
        <div className="create-homework-page">
            <div className="create-homework-header">
                <h1>{isEditMode ? 'Редактировать ДЗ' : 'Создать домашнее задание'}</h1>
                <button 
                    onClick={() => navigate('/admin/homework')}
                    className="btn-back"
                >
                    <FiX />
                    Отмена
                </button>
            </div>

            <form onSubmit={handleSubmit} className="homework-form">
                <div className="form-section">
                    <label className="form-label">
                        Описание задания *
                    </label>
                    <div className="quill-wrapper">
                        <ReactQuill
                            value={description}
                            onChange={setDescription}
                            modules={quillModules}
                            placeholder="Введите описание домашнего задания. Вы можете вставлять ссылки на уроки и блоки..."
                            theme="snow"
                        />
                    </div>
                    <p className="form-hint">
                        💡 Совет: Скопируйте ссылку на блок урока из просмотра урока и вставьте её сюда
                    </p>
                </div>

                <div className="form-section">
                    <label className="form-label">Группы учеников</label>
                    <StudentGroupPicker
                        selectedGroupIds={selectedGroups}
                        onChange={setSelectedGroups}
                    />
                </div>

                <div className="form-section">
                    <div className="students-header">
                        <label className="form-label">
                            Или выберите студентов отдельно ({selectedStudents.length} выбрано)
                        </label>
                        <div className="students-actions">
                            <button
                                type="button"
                                onClick={selectAllStudents}
                                className="btn-select-all"
                            >
                                Выбрать всех
                            </button>
                            <button
                                type="button"
                                onClick={deselectAllStudents}
                                className="btn-deselect-all"
                            >
                                Снять все
                            </button>
                        </div>
                    </div>

                    <div className="students-grid">
                        {students.map(student => (
                            <label
                                key={student.id}
                                className={`student-checkbox ${selectedStudents.includes(student.id) ? 'selected' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedStudents.includes(student.id)}
                                    onChange={() => toggleStudent(student.id)}
                                />
                                <span className="student-name">{student.username}</span>
                                <span className="student-email">{student.email}</span>
                            </label>
                        ))}
                    </div>

                    {students.length === 0 && (
                        <p className="no-students">Студенты не найдены</p>
                    )}
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/homework')}
                        className="btn-cancel"
                        disabled={saving}
                    >
                        Отмена
                    </button>
                    <button
                        type="submit"
                        className="btn-save"
                        disabled={saving}
                    >
                        <FiSave />
                        {saving ? 'Сохранение...' : isEditMode ? 'Сохранить' : 'Создать'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateHomeworkPage;
